import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Grid, TextField, Typography } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { useAppSnackbar } from '../../common/TableDesign';
import ModulePageHeader from '../../common/ModulePageHeader';
import DocResultDialog from './DocResultDialog';
import StockLinesEditor from './StockLinesEditor';
import { fmtDate, getErrorMessage, itemLabelOf } from './InventoryShared';

const emptyLine = () => ({ item_id: '', quantity_delta: '', unit_cost: '' });
const emptyForm = () => ({
  adjustment_date: new Date().toISOString().slice(0, 10),
  reason: '',
  lines: [emptyLine()],
});

// Stock adjustments: correct book quantities after a stocktake (verified
// against CreateStockAdjustmentDto). Lines carry a SIGNED quantity_delta
// (+ surplus, - shortage); unit_cost is optional and defaults to the current
// average at posting. Surplus posts DR inventory / CR adjustment gain,
// shortage posts DR adjustment loss / CR inventory.
const AdjustmentsTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.INVENTORY, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.INVENTORY, ACTIONS.APPROVE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();

  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  if (!can(RESOURCES.LEDGER.INVENTORY, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view Stock Adjustments.</Alert>;
  }

  const set = (key, value) => setValues((p) => ({ ...p, [key]: value }));
  const idOf = (d) => d?.adjustment_id ?? d?.id;
  const numberOf = (d) => d?.adjustment_number || (idOf(d) ? `ADJ-${idOf(d)}` : '');

  const submitCreate = async () => {
    const lines = values.lines.filter((l) => l.item_id || l.quantity_delta !== '');
    if (lines.length < 1) {
      showSnackbar('At least one line is required', 'error');
      return;
    }
    if (lines.some((l) => !l.item_id || !Number.isFinite(Number(l.quantity_delta)) || Number(l.quantity_delta) === 0)) {
      showSnackbar('Each line needs an item and a non-zero quantity (negative counts down)', 'error');
      return;
    }
    if (lines.some((l) => l.unit_cost !== '' && !(Number(l.unit_cost) > 0))) {
      showSnackbar('Unit costs must be greater than zero', 'error');
      return;
    }
    try {
      setSaving(true);
      const res = await ledgerAPI.createInvAdjustment({
        adjustment_date: values.adjustment_date,
        ...(values.reason?.trim() ? { reason: values.reason.trim() } : {}),
        lines: lines.map((l) => ({
          item_id: Number(l.item_id),
          quantity_delta: Number(l.quantity_delta),
          ...(l.unit_cost !== '' ? { unit_cost: Number(l.unit_cost) } : {}),
        })),
      });
      const doc = res?.data?.data ?? res?.data ?? {};
      setResult({ ...doc, id: idOf(doc) ?? doc.adjustment_id });
      setShowCreate(false);
      setValues(emptyForm());
      showSnackbar(`Adjustment ${numberOf(doc)} created as DRAFT`, 'success');
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <ModulePageHeader
        title="Stock Adjustments"
        subtitle="Stocktake corrections - signed quantities, surplus up, shortage down."
        actions={canCreate ? [
          <Button key="add" variant="contained" startIcon={<AddIcon />} onClick={() => { setValues(emptyForm()); setShowCreate(true); }}>
            New Adjustment
          </Button>,
        ] : null}
      />
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Adjustments are tracked by document number (returned on creation). Approve then post to update stock and the GL.
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="lg" fullWidth>
        <DialogTitle>New Stock Adjustment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1, mb: 2 }}>
            <Grid size={{ xs: 6, md: 4 }}>
              <TextField
                fullWidth size="small" type="date" label="Adjustment Date" value={values.adjustment_date}
                onChange={(e) => set('adjustment_date', e.target.value)} InputLabelProps={{ shrink: true }} required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth size="small" label="Reason" value={values.reason}
                onChange={(e) => set('reason', e.target.value)}
                helperText="e.g. STOCKTAKE, DAMAGE, EXPIRY - positive qty counts stock up, negative counts it down"
              />
            </Grid>
            <Grid size={12}>
              <StockLinesEditor
                lines={values.lines}
                setLines={(lines) => set('lines', lines)}
                quantityKey="quantity_delta"
                quantityLabel="Qty Delta (+/-)"
                allowNegative
                showUnitCost
                unitCostLabel="Unit Cost (optional)"
                emptyLine={emptyLine}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <DocResultDialog
        open={!!result}
        onClose={() => setResult(null)}
        title="Stock adjustment"
        doc={result}
        setDoc={setResult}
        numberOf={numberOf}
        statusOf={(d) => d.status}
        api={{
          approve: (id) => ledgerAPI.approveInvAdjustment(id),
          post: (id) => ledgerAPI.postInvAdjustment(id),
          reverse: (id, d) => ledgerAPI.reverseInvAdjustment(id, d),
        }}
        canApprove={canApprove}
        renderSummary={(d) => (
          <>
            <Typography variant="body2" color="text.secondary">{fmtDate(d.adjustment_date)} {d.reason ? `- ${d.reason}` : ''}</Typography>
            {(d.lines || []).map((l, i) => (
              <Typography key={l.line_id ?? i} variant="body2">
                {l.item ? itemLabelOf(l.item) : `Item ${l.item_id ?? ''}`} - delta {l.quantity_delta}
              </Typography>
            ))}
          </>
        )}
      />

      {SnackbarComponent}
    </Box>
  );
};

export default AdjustmentsTab;
